import React from 'react';
import PropTypes from 'prop-types';
import {resource} from '@albert-io/json-api-framework/request/builder';
import {AuthoringSubjectModelV1} from 'resources/augmented/AuthoringSubject/AuthoringSubjectModel.v1';
import {callAction, callTargetedAction, setUpStore} from 'client/framework';
import getDifficultyValueFromInt from 'lib/getDifficultyValueFromInt';
import {getQuestionTypeDefinition} from 'client/QuestionTypes/QuestionTypeDefinitions';
import questionEditorStore from '../QuestionEditor/QuestionEditor.store';
import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';
import ConfirmLeaveModal from 'generic/ConfirmLeaveModal/ConfirmLeaveModal.react';
import QuestionDifficultyLabel from 'generic/QuestionDifficultyLabel/QuestionDifficultyLabel.react';
import supplementActions from 'client/Supplement/SupplementActions';
import textHighlightQuestionActions from 'client/QuestionTypes/TextHighlight/V2/TextHighlightQuestion.actions';
import TextHighlightQuestionStore from 'client/QuestionTypes/TextHighlight/V2/TextHighlightQuestion.store';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import './question-preview.scss';

function getAuthoringSubjectQuery() {
  return resource('authoring_subject_v1').mandarkEndpoint([
    'authoring_subjects_v1',
    questionEditorStore.getQuestionSet().getSubjectId()
  ]);
}

export default awaitMandarkQueries(
  () => {
    const queries = {};
    if (!questionEditorStore.getQuestionSet().existsOnServer()) {
      queries.subject = getAuthoringSubjectQuery();
    }
    return {
      queries
    };
  },
  class QuestionPreview extends React.Component {
    static propTypes = {
      questionId: PropTypes.string,
      questionSetId: PropTypes.string,
      subject: PropTypes.instanceOf(AuthoringSubjectModelV1)
    };

    constructor(props) {
      super(props);

      if (questionEditorStore.getQuestion().getQuestionType() === 'text-highlight') {
        this.setUpTextHighlightQuestion();
      }
    }
    /**
     * We call `setUpSupplements` regardless of prop or store state.
     * This is because we are not only relying on the `questionId` but also
     * the `questionEditorStore.isReady()` boolean.
     */
    UNSAFE_componentWillMount = this.setUpSupplements;
    UNSAFE_componentWillUpdate = this.setUpSupplements;

    setUpSupplements() {
      const supplements = questionEditorStore.getQuestion().getAuthoringSupplements();
      callAction(supplementActions.QUESTIONSET_SUPPLEMENTS_RECEIVED, supplements);
    }

    getStoreName() {
      return `QuestionPreview--${this.props.questionId}`;
    }

    setUpTextHighlightQuestion() {
      const {questionId} = this.props;
      const storeName = this.getStoreName();
      setUpStore(TextHighlightQuestionStore, storeName, questionId);

      const question = questionEditorStore.getQuestion();
      callTargetedAction({
        name: textHighlightQuestionActions.SET_UP_QUESTION,
        payload: {
          highlightPrompt: question.getHighlightPrompt(),
          preferredPermutation: question.getPreferredPermutation(),
          rubric: question.getRubric()
        },
        targetStore: storeName
      });
    }

    shouldPromptLeave() {
      const currentQuestion = questionEditorStore.getQuestion();
      if (!currentQuestion) {
        return false;
      }
      return !currentQuestion.getChangeMap().isEmpty();
    }

    render() {
      if (!questionEditorStore.isReady()) {
        return null;
      }

      const question = questionEditorStore.getQuestion();
      const questionSet = questionEditorStore.getQuestionSet();
      const subject = !questionSet.getAuthoringSubject().isEmpty()
        ? questionSet.getAuthoringSubject()
        : this.props.subject;
      const type = getQuestionTypeDefinition(question.getType());
      const QuestionRenderer = type.get('questionRenderer');
      const ExplanationRenderer = type.get('explanationRenderer');
      const ResponseStatsRenderer = type.get('responseStatsRenderer');
      const correctAnswer = question.getValidResponse();
      const statsContent = (
        <ResponseStatsRenderer
          question={question}
          correctAnswer={correctAnswer}
          previewMode={true}
          selectedAnswer={correctAnswer}
          storeName={`QuestionPreviewResponseStatsStore${question.getId()}`}
        />
      );
      const subjectSlug = questionSet.getSlugId().split('@')[0];
      const storeName = this.getStoreName();

      return (
        <div className='question-preview card'>
          <div className='question-preview__header'>
            <h1>
              <MarkdownRendererV2 text={question.getTitle()} />
            </h1>
            <QuestionDifficultyLabel
              difficulty={getDifficultyValueFromInt(question.getDifficulty(), true)}
            />
          </div>
          <QuestionRenderer
            isGuessSubmitted={false}
            question={question}
            questionId={this.props.questionId}
            storeName={storeName}
          />
          <ExplanationRenderer
            question={question}
            questionId={this.props.questionId}
            questionSetId={this.props.questionSetId}
            isCorrect={true}
            responseStats={statsContent}
            showLabelTable={subject.isShouldShowLabelTable()}
            solutionText={question.getSolutionText()}
            subjectId={questionSet.getSubjectId()}
            subjectSlug={subjectSlug}
            videoExplanationId={question.getVideoExplanationId()}
            questionStoreName={storeName}
            isInUnpublishedSubject={!subject.isPublished()}
          />
          <ConfirmLeaveModal
            shouldPromptFunc={this.shouldPromptLeave}
            storeName='questionPreviewIERouteChangeHandler'
            modalTitle='You have unsaved changes'
            modalPrompt={
              <p>
                There are unsaved changes on this question.
                <br />
                Are you sure you want to leave?
              </p>
            }
          />
        </div>
      );
    }
  }
);
